import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip } from 'recharts';
import { AlertCircle, Loader2, Filter, ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useDashboardSpendingByCategory } from '../hooks/useDashboardSpending';
import { getCurrentUserId } from '../../../lib/supabase';
import { useRegion } from '../../../contexts/RegionContext';
import { getCategoryIconComponent } from '../../../utils/icon-utils';
import { CategoryType } from '../../../types/category.types';
import { categoryService } from '../../../services/category-service';
import {
  DateRangeSelector,
  type DateRange,
  getPresetLabel,
} from './DateRangeSelector';
import { format, startOfMonth, endOfMonth, subMonths } from 'date-fns';

interface SpendingByCategoryCardProps {
  dateFrom?: string;
  dateTo?: string;
  onDateRangeChange?: (range: { from: string; to: string }) => void;
  className?: string;
}

function SpendingTooltip({
  active,
  payload,
  formatter,
}: {
  active?: boolean;
  payload?: any[];
  formatter: (value: number) => string;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const item = payload[0].payload;
  const color = item.color || FALLBACK_COLOR;
  const Icon = getCategoryIconComponent({
    icon: item.icon,
    slug: item.slug,
    name: item.name,
    type: item.type,
  });

  return (
    <div className="z-50 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-lg dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-center space-x-2">
        <span
          className="inline-flex h-2.5 w-2.5 flex-shrink-0 rounded-full"
          style={{ backgroundColor: color }}
          aria-hidden="true"
        />
        <p className="font-medium text-gray-900 dark:text-gray-50">{item.name}</p>
        {Icon && (
          <Icon className="h-3.5 w-3.5" style={{ color }} aria-hidden="true" />
        )}
      </div>
      <p className="text-gray-600 dark:text-gray-300">
        {formatter(item.amount)} • {item.percentage.toFixed(1)}%
      </p>
    </div>
  );
}

function hexToRgba(hex: string | undefined, alpha: number): string | undefined {
  if (!hex) return undefined;
  let sanitized = hex.trim();
  if (sanitized.startsWith('#')) {
    sanitized = sanitized.slice(1);
  }
  if (sanitized.length === 3) {
    sanitized = sanitized
      .split('')
      .map((char) => char + char)
      .join('');
  }
  if (sanitized.length !== 6) {
    return undefined;
  }
  const r = Number.parseInt(sanitized.slice(0, 2), 16);
  const g = Number.parseInt(sanitized.slice(2, 4), 16);
  const b = Number.parseInt(sanitized.slice(4, 6), 16);
  if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) {
    return undefined;
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const FALLBACK_COLOR = '#6366F1';

export function SpendingByCategoryCard({ dateFrom, dateTo, onDateRangeChange, className }: SpendingByCategoryCardProps) {
  const [userId, setUserId] = React.useState<string | null>(null);
  const navigate = useNavigate();
  const region = useRegion();
  const formatCurrency = React.useMemo(() => {
    if (!region?.formatCurrency) {
      // Fallback formatter if formatCurrency is not available
      return (amount: number) => {
        return new Intl.NumberFormat('en-AU', {
          style: 'currency',
          currency: 'AUD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);
      };
    }
    return region.formatCurrency;
  }, [region]);

  // Default to current month if no dates provided
  const initialRange = React.useMemo(() => {
    if (dateFrom && dateTo) {
      return { from: dateFrom, to: dateTo };
    }

    const now = new Date();
    const lastMonth = subMonths(now, 1);
    return {
      from: format(startOfMonth(lastMonth), 'yyyy-MM-dd'),
      to: format(endOfMonth(lastMonth), 'yyyy-MM-dd'),
    };
  }, [dateFrom, dateTo]);
  
  const [dateRange, setDateRange] = React.useState<DateRange>(initialRange);
  const [grouping, setGrouping] = React.useState<'parent' | 'category'>('parent');
  const [showExcluded, setShowExcluded] = React.useState(false);
  const [showFilters, setShowFilters] = React.useState(false);
  const [isGroupingDropdownOpen, setIsGroupingDropdownOpen] = React.useState(false);

  React.useEffect(() => {
    getCurrentUserId().then(setUserId);
  }, []);

  React.useEffect(() => {
    if (dateFrom && dateTo) {
      setDateRange({ from: dateFrom, to: dateTo });
    }
  }, [dateFrom, dateTo]);

  const handleDateRangeChange = (range: DateRange) => {
    setDateRange(range);
    onDateRangeChange?.(range);
  };

  // Ensure dateRange is always valid before using it
  const validDateRange = React.useMemo(() => {
    if (!dateRange || !dateRange.from || !dateRange.to) {
      return initialRange;
    }
    return dateRange;
  }, [dateRange, initialRange]);

  const periodLabel = React.useMemo(() => getPresetLabel(validDateRange), [validDateRange]);

  const {
    categories,
    total,
    isLoading,
    isError,
    error,
    isFetching,
    transactionCount,
  } = useDashboardSpendingByCategory({
    userId: userId ?? '',
    dateFrom: validDateRange.from,
    dateTo: validDateRange.to,
    grouping,
    includeExcluded: showExcluded,
  });

  const {
    data: excludedCategories,
    isFetching: _isFetchingExcluded,
  } = useQuery({
    queryKey: ['categories', 'excluded-for-insights', userId],
    enabled: !!userId,
    queryFn: async () => {
      const result = await categoryService.listCategories({ excludeFromSpending: true });
      if (result.error) {
        throw new Error(result.error.message);
      }
      return result.data?.categories ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const excludedCount = excludedCategories?.length ?? 0;
  const hasExcludedCategories = excludedCount > 0;

  const hasData = categories.length > 0 && total > 0;

  const handleGroupingChange = (mode: 'parent' | 'category') => {
    setGrouping(mode);
  };

  const handleToggleExcluded = (value: boolean) => {
    setShowExcluded(value);
  };

  const groupingButtons: Array<{ value: 'parent' | 'category'; label: string }> = [
    { value: 'parent', label: 'By Category' },
    { value: 'category', label: 'By Subcategory' },
  ];

  const handleManageExclusions = () => {
    navigate('/settings/categorisation?tab=categories&insights=excluded');
  };

  return (
    <div className={clsx('card h-full', className)}>
      <div className="card-header">
        <div>
          <h2 className="card-title">Spending by category</h2>
          <p className="card-description">
            Where your money went this period
            <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700/50 dark:text-gray-200">
              {periodLabel}
            </span>
            {transactionCount !== undefined && (
              <span className="ml-2 inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-700/50 dark:text-gray-200">
                {transactionCount.toLocaleString()} transactions
              </span>
            )}
          </p>
        </div>

        <div className="flex flex-col gap-4">
          {/* Mobile Filter Toggle Button - Only visible on mobile */}
          <div className="md:hidden">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={clsx(
                'flex items-center space-x-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors duration-200',
                showFilters
                  ? 'bg-primary-100 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 border border-primary-200 dark:border-primary-800'
                  : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600 hover:bg-gray-200 dark:hover:bg-gray-600'
              )}
              aria-expanded={showFilters}
              aria-controls="spending-filters"
            >
              <Filter className="h-4 w-4" />
              <span>Filters</span>
            </button>
          </div>

          {/* Filter Controls - Hidden on mobile, visible on desktop */}
          <div 
            id="spending-filters"
            className="hidden md:flex flex-wrap items-center justify-start gap-3"
          >
            <DateRangeSelector
              value={validDateRange}
              onChange={handleDateRangeChange}
              className="h-11"
            />
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsGroupingDropdownOpen(!isGroupingDropdownOpen)}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors h-11"
                aria-label="Select spending grouping"
                aria-expanded={isGroupingDropdownOpen}
              >
                <span className="whitespace-nowrap">
                  {groupingButtons.find(b => b.value === grouping)?.label || 'By Category'}
                </span>
                <ChevronDown className={clsx('h-4 w-4 transition-transform flex-shrink-0', isGroupingDropdownOpen && 'rotate-180')} aria-hidden="true" />
              </button>

              {isGroupingDropdownOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsGroupingDropdownOpen(false)}
                    aria-hidden="true"
                  />
                  <div className="absolute left-0 mt-1 w-48 rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 z-20">
                    <div className="py-1">
                      {groupingButtons.map((button) => {
                        const isActive = grouping === button.value;
                        return (
                          <button
                            key={button.value}
                            type="button"
                            onClick={() => {
                              handleGroupingChange(button.value);
                              setIsGroupingDropdownOpen(false);
                            }}
                            className={clsx(
                              'w-full text-left px-4 py-2 text-sm transition-colors',
                              isActive
                                ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400 font-medium'
                                : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
                            )}
                          >
                            {button.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </>
              )}
            </div>
            {isFetching && (
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-label="Updating spending data" />
            )}
          </div>

          {/* Mobile Filter Panel - Only shown when expanded on mobile */}
          {showFilters && (
            <div className="md:hidden pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-col gap-4">
                <DateRangeSelector
                  value={validDateRange}
                  onChange={handleDateRangeChange}
                  className="h-11"
                />
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setIsGroupingDropdownOpen(!isGroupingDropdownOpen)}
                    className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors h-11 w-full"
                    aria-label="Select spending grouping"
                    aria-expanded={isGroupingDropdownOpen}
                  >
                    <span className="whitespace-nowrap flex-1 text-left">
                      {groupingButtons.find(b => b.value === grouping)?.label || 'By Category'}
                    </span>
                    <ChevronDown className={clsx('h-4 w-4 transition-transform flex-shrink-0', isGroupingDropdownOpen && 'rotate-180')} aria-hidden="true" />
                  </button>

                  {isGroupingDropdownOpen && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setIsGroupingDropdownOpen(false)}
                        aria-hidden="true"
                      />
                      <div className="absolute left-0 right-0 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800 z-20">
                        <div className="py-1">
                          {groupingButtons.map((button) => {
                            const isActive = grouping === button.value;
                            return (
                              <button
                                key={button.value}
                                type="button"
                                onClick={() => {
                                  handleGroupingChange(button.value);
                                  setIsGroupingDropdownOpen(false);
                                }}
                                className={clsx(
                                  'w-full text-left px-4 py-2 text-sm transition-colors',
                                  isActive
                                    ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400 font-medium'
                                    : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700'
                                )}
                              >
                                {button.label}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  )}
                </div>
                {isFetching && (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-label="Updating spending data" />
                    <span className="text-sm text-gray-600 dark:text-gray-400">Updating...</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card-content">
        {isLoading || userId === null ? (
          <div className="flex h-48 items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary-500" aria-label="Loading spending data" />
          </div>
        ) : isError ? (
          <div className="flex h-48 flex-col items-center justify-center space-y-2 text-center">
            <AlertCircle className="h-8 w-8 text-red-500" aria-hidden="true" />
            <p className="text-sm text-red-600 dark:text-red-400">
              {error instanceof Error ? error.message : 'Unable to load spending data.'}
            </p>
          </div>
        ) : !hasData ? (
          <div className="flex h-48 flex-col items-center justify-center space-y-2 text-center">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-200">No spending recorded</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Import transactions or categorise expenses to see your spending breakdown.
            </p>
          </div>
        ) : (
          <div className="main-page-grid-split">
            <div className="h-[20.8rem] relative min-w-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={categories as unknown as Array<{ name: string; amount: number; [key: string]: unknown }>}
                    dataKey="amount"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius="55%"
                    outerRadius="80%"
                    paddingAngle={2}
                  >
                    {categories.map((category) => (
                      <Cell key={category.name} fill={category.color} aria-label={category.name} />
                    ))}
                  </Pie>
                  <RechartsTooltip 
                    content={<SpendingTooltip formatter={formatCurrency} />}
                    wrapperStyle={{ zIndex: 50 }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center">
                <div className="text-center">
                  <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Total Spending
                  </p>
                  <p className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    {formatCurrency(total)}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-3 min-w-0">
              {categories.slice(0, 6).map((category) => {
                // Map transaction type to CategoryType enum
                const categoryType = category.type === 'income' 
                  ? CategoryType.INCOME 
                  : category.type === 'expense' 
                  ? CategoryType.EXPENSE 
                  : CategoryType.TRANSFER;
                
                const Icon = getCategoryIconComponent({
                  icon: category.icon,
                  slug: category.slug,
                  name: category.name,
                  type: categoryType,
                });
                
                const handleCategoryClick = () => {
                  const params = new URLSearchParams();
                  params.delete('category');
                  params.delete('categories');
                  const relatedIds = category.relatedCategoryIds
                    ? Array.from(new Set(category.relatedCategoryIds.filter(Boolean)))
                    : [];
                  if (relatedIds.length > 0) {
                    params.set('categories', relatedIds.join(','));
                  } else if (category.categoryId) {
                    params.set('category', category.categoryId);
                  }
                  if (dateRange.from) {
                    params.set('dateFrom', dateRange.from);
                  }
                  if (dateRange.to) {
                    params.set('dateTo', dateRange.to);
                  }
                  navigate(`/transactions?${params.toString()}`);
                };
                const displayColor = category.color || FALLBACK_COLOR;
                const backgroundColor = hexToRgba(displayColor, 0.12) ?? hexToRgba(FALLBACK_COLOR, 0.12) ?? 'rgba(99,102,241,0.12)';

                return (
                  <button
                    key={category.name}
                    onClick={handleCategoryClick}
                    className="w-full flex items-center justify-between rounded-lg border border-gray-100 p-3 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors cursor-pointer text-left"
                    aria-label={`View transactions for ${category.name}`}
                  >
                    <div className="flex items-center space-x-3">
                      <div
                        className="flex h-10 w-10 items-center justify-center rounded-lg"
                        style={{ backgroundColor }}
                      >
                        {Icon ? (
                          <Icon className="h-5 w-5" aria-hidden="true" style={{ color: displayColor }} />
                        ) : (
                          <span className="text-sm font-medium" style={{ color: displayColor }}>
                            {category.name.charAt(0)}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{category.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {category.percentage.toFixed(1)}% • {formatCurrency(category.amount)}
                        </p>
                        {category.isExcluded && (
                          <p className="mt-1 text-xs font-medium text-amber-600 dark:text-amber-300">
                            Excluded from default insights
                          </p>
                        )}
                      </div>
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {formatCurrency(category.amount)}
                    </p>
                  </button>
                );
              })}
              <div className="flex items-center gap-3 flex-wrap">
                {categories.length > 6 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    + {categories.length - 6} more categories
                  </p>
                )}
                {hasExcludedCategories && (
                  <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200">
                    <input
                      type="checkbox"
                      checked={showExcluded}
                      onChange={(e) => handleToggleExcluded(e.target.checked)}
                      className="h-3 w-3 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span>Include {excludedCount} excluded</span>
                    <button
                      type="button"
                      onClick={handleManageExclusions}
                      className="text-primary-600 hover:text-primary-500 dark:text-primary-300 dark:hover:text-primary-200 underline decoration-dotted underline-offset-2"
                    >
                      {excludedCount === 1 ? 'category' : 'categories'}
                    </button>
                  </label>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

